import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import apiCall from '../../api';
import { getItem } from '../helper/localstorage.helper';
import { isGuestInvozioEmail } from '../helper/common.helper';
import { fetchPersonalData } from '../features/profile-settings/profile-settings.slice';
import { updateUser as updateAuthUser } from '../../redux/auth/authSlice';
import '../scss/account-action-modal.style.scss';

interface UpdateGuestEmailModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const OTP_LENGTH = 4;
const OTP_TIMER_SECONDS = 300;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const UpdateGuestEmailModal: React.FC<UpdateGuestEmailModalProps> = ({ show, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [step, setStep] = useState<'enter-email' | 'verify-otp'>('enter-email');
  const [email, setEmail] = useState('');
  const [otpField, setOtpField] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [otpCounter, setOtpCounter] = useState(OTP_TIMER_SECONDS);
  const [loading, setLoading] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (step !== 'verify-otp' || otpCounter <= 0) return;
    const timer = setInterval(() => setOtpCounter((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [step, otpCounter]);

  const resetState = () => {
    setStep('enter-email');
    setEmail('');
    setOtpField(new Array(OTP_LENGTH).fill(''));
    setOtpCounter(OTP_TIMER_SECONDS);
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose();
  };

  const handleSendOtp = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) {
      toast.error(String(t('ENTER_VALID_EMAIL') || 'Please enter a valid email address'));
      return;
    }
    if (isGuestInvozioEmail(trimmedEmail)) {
      toast.error(String(t('GUEST_EMAIL_NOT_ALLOWED') || 'Guest emails are not allowed. Please use your personal email address.'));
      return;
    }

    setLoading(true);
    try {
      const baseUrl = (import.meta as any).env.VITE_API_URL;
      const token = getItem('token');
      const language = getItem('i18nextLng') || 'en';

      const response = await apiCall.post(
        `${baseUrl}/settings/generate-otp`,
        { type: 4, email: trimmedEmail, lang: language },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const statusCode = response?.data?.statusCode;
      if (statusCode === 200 || statusCode === 201) {
        setStep('verify-otp');
        setOtpCounter(OTP_TIMER_SECONDS);
        setOtpField(new Array(OTP_LENGTH).fill(''));
        toast.success(String(t('OTP_SENT_TO_NEW_EMAIL') || 'OTP sent to your new email address'));
      } else {
        toast.error(String(response?.data?.message || t('Something went wrong') || 'Something went wrong'));
      }
    } catch (error: any) {
      toast.error(String(error?.response?.data?.message || t('Something went wrong') || 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otp: string) => {
    setLoading(true);
    try {
      const baseUrl = (import.meta as any).env.VITE_API_URL;
      const token = getItem('token');
      const trimmedEmail = email.trim();

      const response = await apiCall.put(
        `${baseUrl}/employee/verify-new-v2?code=${otp}&email=${encodeURIComponent(trimmedEmail)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const statusCode = response?.data?.statusCode;
      if (statusCode === 200 || statusCode === 201) {
        toast.success(String(t('EMAIL_VERIFIED_SUCCESSFULLY') || 'Email verified successfully'));

        const userDataString = localStorage.getItem('user');
        const parsedUser = userDataString ? JSON.parse(userDataString) : null;
        const userId = parsedUser?.userData?.userId;

        if (parsedUser?.userData) {
          parsedUser.userData.email = trimmedEmail;
          parsedUser.userData.isVerified = true;
          localStorage.setItem('user', JSON.stringify(parsedUser));
          dispatch(updateAuthUser(parsedUser.userData));
          window.dispatchEvent(new Event('userUpdated'));
        }

        if (userId) {
          dispatch(fetchPersonalData(userId) as any);
        }

        resetState();
        onSuccess?.();
        onClose();
      } else {
        toast.error(String(response?.data?.message || t('Something went wrong') || 'Something went wrong'));
      }
    } catch (error: any) {
      toast.error(String(error?.response?.data?.message || t('Something went wrong') || 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    setOtpField(new Array(OTP_LENGTH).fill(''));
    setOtpCounter(OTP_TIMER_SECONDS);
    handleSendOtp();
  };

  const handleOtpChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && isNaN(Number(value))) return;
    
    const newOtp = [...otpField];
    newOtp[index] = value.substring(value.length - 1);
    setOtpField(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }

    if (value && index === OTP_LENGTH - 1 && newOtp.every((digit) => digit)) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  if (!show) return null;

  return (
    <div className="account-action-modal-overlay" onClick={handleClose}>
      <div className="account-action-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{step === 'enter-email' ? t('VERIFY_EMAIL') : t('VERIFY_OTP')}</h2>
          <button className="modal-close-btn" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#252525" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="sad-message-badge">
            <span className="emoji">📧</span>
            <span className="message">{t('CHANGE_EMAIL')}</span>
          </div>

          {step === 'enter-email' ? (
            <>
              <div className="modal-message">
                <p>{t('EMAIL_CHANGE_DESCRIPTION') || 'Enter your new email address and verify it with OTP'}</p>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#fef3cd',
                    border: '1px solid #fecf33',
                    borderRadius: '6px',
                    marginTop: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ color: '#b45309', fontSize: '16px' }}>⚠️</span>
                  <span style={{ color: '#b45309', fontSize: '14px', fontWeight: 500 }}>
                    {t('GUEST_EMAIL_WARNING')}
                  </span>
                </div>
              </div>

              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={String(t('ENTER_NEW_EMAIL') || 'Enter your new email address')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '2px solid #d0d5dd',
                  borderRadius: '8px',
                  outline: 'none',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                }}
              />

              <div className="modal-actions-group">
                <button
                  className="modal-action-btn"
                  onClick={handleSendOtp}
                  disabled={loading || !email}
                  style={{ opacity: loading || !email ? 0.5 : 1, cursor: loading || !email ? 'not-allowed' : 'pointer' }}
                >
                  <span>{loading ? t('SENDING') : t('SEND_OTP')}</span>
                </button>
                <button
                  className="modal-cancel-btn"
                  onClick={handleClose}
                  disabled={loading}
                  style={{ opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  <span>{t('CANCEL')}</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="modal-message">
                <p>{t('OTP_SENT_TO_NEW_EMAIL') || 'OTP sent to your new email address'}</p>
                {otpCounter > 0 && otpCounter < OTP_TIMER_SECONDS && (
                  <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '12px' }}>
                    {t('RESEND_OTP_IN')} <strong>{otpCounter}s</strong>
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                {otpField.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { otpInputRefs.current[index] = el; }}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !otpField[index] && index > 0) {
                        otpInputRefs.current[index - 1]?.focus();
                      }
                    }}
                    style={{
                      width: '50px',
                      height: '50px',
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      border: '2px solid #d0d5dd',
                      borderRadius: '8px',
                      outline: 'none',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                    }}
                    disabled={loading}
                  />
                ))}
              </div>

              <div className="modal-actions-group">
                <button
                  className="modal-action-btn"
                  onClick={() => handleVerifyOtp(otpField.join(''))}
                  disabled={loading || otpField.join('').length < OTP_LENGTH}
                  style={{
                    opacity: loading || otpField.join('').length < OTP_LENGTH ? 0.5 : 1,
                    cursor: loading || otpField.join('').length < OTP_LENGTH ? 'not-allowed' : 'pointer',
                  }}
                >
                  <span>{loading ? t('VERIFYING') : t('VERIFY')}</span>
                </button>
                <button
                  className="modal-cancel-btn"
                  onClick={handleResendOtp}
                  disabled={loading || otpCounter > 0}
                  style={{ opacity: loading || otpCounter > 0 ? 0.5 : 1, cursor: loading || otpCounter > 0 ? 'not-allowed' : 'pointer' }}
                >
                  <span>{otpCounter > 0 ? `${t('RESEND_IN')} ${otpCounter}s` : t('RESEND')}</span>
                </button>
                <button
                  className="modal-cancel-btn"
                  onClick={handleClose}
                  disabled={loading}
                  style={{ opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  <span>{t('CANCEL')}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateGuestEmailModal;